import React, { useState, useEffect, useRef } from 'react';
import './FileStorage.css';

const FileStorage = ({ category, title, description }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDescription, setUploadDescription] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFiles();
  }, [category]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/data-room/files/${category}`, {credentials: 'include'});
      if (response.ok) {
        const data = await response.json();
        setFiles(data);
      } else {
        console.error('Failed to fetch files');
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!uploadDescription.trim()) {
      alert('Please provide a description for the file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', uploadDescription);
    formData.append('category', category);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/data-room/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        alert('File uploaded successfully');
        setShowUploadModal(false);
        setUploadDescription('');
        fetchFiles();
      } else {
        const errorText = await response.text();
        alert(`Failed to upload file: ${errorText}`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/data-room/files/${fileId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('File deleted successfully');
        fetchFiles();
      } else {
        const errorText = await response.text();
        alert(`Failed to delete file: ${errorText}`);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error deleting file');
    }
  };

  const handleViewFile = async (fileId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/data-room/files/${fileId}/view`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        alert('Failed to view file');
      }
    } catch (error) {
      console.error('Error viewing file:', error);
      alert('Error viewing file');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="file-storage-container">
      <div className="file-storage-header">
        <div>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        <button 
          className="upload-button"
          onClick={() => setShowUploadModal(true)}
        >
          Upload File
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading files...</div>
      ) : (
        <div className="files-table-container">
          {files.length === 0 ? (
            <div className="no-files">
              <p>No files uploaded yet</p>
              <button 
                className="upload-button"
                onClick={() => setShowUploadModal(true)}
              >
                Upload Your First File
              </button>
            </div>
          ) : (
            <table className="files-table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Version</th>
                  <th>Description</th>
                  <th>Size</th>
                  <th>Date Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.id}>
                    <td className="file-name">{file.originalName}</td>
                    <td className="version">v{file.version}</td>
                    <td className="description">{file.description}</td>
                    <td className="size">{formatFileSize(file.size)}</td>
                    <td className="date">{new Date(file.createdAt).toLocaleDateString()}</td>
                    <td className="actions">
                      <button 
                        className="view-button"
                        onClick={() => handleViewFile(file.id)}
                      >
                        View
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteFile(file.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="upload-modal">
            <h3>Upload New File</h3>
            <div className="upload-form">
              <div className="form-group">
                <label>File Description *</label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Describe the contents and purpose of this file..."
                  rows={3}
                  required
                />
              </div>
              <div className="form-group">
                <label>Select File *</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  required
                />
              </div>
              <div className="modal-actions">
                <button 
                  className="cancel-button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadDescription('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="upload-submit-button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose File & Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileStorage;